"use client";

import { useEffect, useState } from "react";
import { Loader2, Settings } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useCommandProjectDetail } from "@/hooks/use-command-projects";
import type {
  CommandProject,
  CommandProjectVisibility,
  UpdateCommandProjectPayload,
} from "@/types/command-agent";

interface EditCommandProjectDialogProps {
  project: CommandProject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (projectId: string, payload: UpdateCommandProjectPayload) => void;
  isPending?: boolean;
}

export function EditCommandProjectDialog({
  project,
  open,
  onOpenChange,
  onSave,
  isPending,
}: EditCommandProjectDialogProps) {
  const { data: detail, isLoading: isLoadingDetail } = useCommandProjectDetail(
    open && project ? project.id : null
  );

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [visibility, setVisibility] = useState<CommandProjectVisibility>("PERSONAL");

  useEffect(() => {
    if (!open || !project) return;
    const source = detail ?? project;
    setName(source.name);
    setDescription(source.description ?? "");
    setInstructions(source.instructions ?? "");
    setVisibility(source.visibility);
  }, [open, project, detail]);

  const submit = () => {
    const trimmedName = name.trim();
    if (!project || trimmedName.length < 2 || isPending) return;

    onSave(project.id, {
      name: trimmedName,
      description: description.trim() || null,
      instructions: instructions.trim() || null,
      visibility,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] bg-violet-100 dark:bg-violet-900/30">
              <Settings className="size-4 text-primary" />
            </div>
            <div className="space-y-1">
              <DialogTitle>Project settings</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Update how this project appears and how Command uses it.
              </p>
            </div>
          </div>
        </DialogHeader>

        {isLoadingDetail && !detail ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="project-settings-name">Name</Label>
              <Input
                id="project-settings-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Project name"
                maxLength={100}
                disabled={isPending}
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="project-settings-description">Description</Label>
              <Textarea
                id="project-settings-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Optional short description"
                rows={2}
                maxLength={500}
                disabled={isPending}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="project-settings-instructions">Instructions</Label>
              <Textarea
                id="project-settings-instructions"
                value={instructions}
                onChange={(event) => setInstructions(event.target.value)}
                placeholder="Optional context for chats in this project"
                rows={4}
                maxLength={4000}
                disabled={isPending}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Visibility</Label>
              <Select
                value={visibility}
                onValueChange={(value) =>
                  setVisibility(value as CommandProjectVisibility)
                }
                disabled={isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERSONAL">Personal</SelectItem>
                  <SelectItem value="TEAM">Team</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={name.trim().length < 2 || isPending || isLoadingDetail}
            type="button"
          >
            {isPending ? <Loader2 className="size-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
